import { Component, computed, input, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';

import {
  WidgetDelta,
  WidgetListItem,
  WidgetSecondaryStat,
} from '../../../features/dashboard/widgets/dashboard-widget.model';
import { CardHeaderComponent } from '../../../features/dashboard/widgets/card-header.component';

@Component({
  selector: 'app-stat-tile',
  standalone: true,
  imports: [RouterLink, NgTemplateOutlet, CardHeaderComponent],
  template: `
    <div class="relative h-full">
      <!-- The tile body is shared; only the outer element (link vs button) differs. -->
      <ng-template #body>
        <app-card-header [title]="label()" [icon]="icon()" [accent]="accent()" />

        <div class="flex flex-1 flex-col justify-center gap-1">
          @if (loading()) {
          <span class="my-1 h-8 w-16 animate-pulse rounded bg-surface-200 dark:bg-surface-700"></span>
          } @else {
          <div class="flex items-baseline gap-1">
            <span class="text-3xl font-extrabold tracking-tight text-text-main">{{ value() }}</span>
            @if (unit()) {
            <span class="text-sm font-medium text-text-muted">{{ unit() }}</span>
            }
          </div>
          }

          @if (!loading() && delta(); as d) {
          <span class="flex items-center gap-1 text-xs font-medium" [class]="deltaClass(d)">
            <i [class]="d.direction === 'up' ? 'pi pi-arrow-up' : 'pi pi-arrow-down'" class="text-[0.6rem]"></i>
            <span>{{ d.value }}</span>
            @if (d.label) {
            <span class="text-text-muted/80">{{ d.label }}</span>
            }
          </span>
          } @else if (hasOverlay()) {
          <span class="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400">
            <i class="pi pi-chart-pie text-[0.6rem]"></i> View breakdown
          </span>
          }
        </div>

        @if (!loading() && secondary(); as s) {
        <!-- Companion count, same card — a second line, not a divided column. -->
        <div class="flex items-center gap-1.5 border-t border-surface-100 pt-2 text-xs dark:border-surface-800">
          @if (s.icon) {
          <i [class]="s.icon" class="text-text-muted"></i>
          }
          <span class="font-semibold text-text-main">{{ s.value }}</span>
          <span class="text-text-muted">{{ s.label }}</span>
        </div>
        }
      </ng-template>

      @if (hasOverlay()) {
      <button
        type="button"
        (click)="open.set(!open())"
        class="card flex h-full w-full flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 text-left transition-colors hover:border-primary-300 dark:border-surface-700/40 dark:bg-surface-900 dark:hover:border-primary-700"
      >
        <ng-container [ngTemplateOutlet]="body" />
      </button>

      @if (open()) {
      <!-- Click-away backdrop, then the breakdown panel. -->
      <div class="fixed inset-0 z-10" (click)="open.set(false)"></div>
      <div
        class="absolute right-0 top-full z-20 mt-2 w-64 rounded-xl border border-surface-200/70 bg-surface-0 p-3 shadow-lg dark:border-surface-700/50 dark:bg-surface-900"
      >
        <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">{{ label() }} — breakdown</p>
        <ul class="flex flex-col divide-y divide-surface-200/60 dark:divide-surface-700/40">
          @for (item of overlayItems(); track item.label) {
          <li class="flex items-center justify-between py-1.5 text-sm">
            <span class="text-text-main">{{ item.label }}</span>
            <span class="font-semibold text-text-muted">{{ item.tag }}</span>
          </li>
          }
        </ul>
      </div>
      }
      } @else {
      <a
        [routerLink]="link()"
        class="card flex h-full w-full flex-col gap-3 rounded-xl border border-surface-200/60 bg-surface-0 p-5 transition-colors hover:border-primary-300 dark:border-surface-700/40 dark:bg-surface-900 dark:hover:border-primary-700"
      >
        <ng-container [ngTemplateOutlet]="body" />
      </a>
      }
    </div>
  `,
})
export class StatTileComponent {
  /** Big headline value. Pass '—' when the source is unavailable. */
  value = input.required<string | number>();
  label = input.required<string>();
  unit = input<string>('');
  icon = input<string>('');
  accent = input<string>('');
  link = input.required<string | any[]>();
  loading = input<boolean>(false);
  delta = input<WidgetDelta | undefined>(undefined);
  /** A companion count folded into this same card (e.g. Open Vacancies). */
  secondary = input<WidgetSecondaryStat | undefined>(undefined);
  /** When present, the tile toggles a breakdown panel instead of navigating. */
  overlayItems = input<WidgetListItem[] | undefined>(undefined);

  open = signal(false);
  hasOverlay = computed(() => (this.overlayItems()?.length ?? 0) > 0);

  deltaClass(d: WidgetDelta): string {
    switch (d.sentiment) {
      case 'good':
        return 'text-green-600 dark:text-green-400';
      case 'bad':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-text-muted';
    }
  }
}
